import { InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import Link from "next/link";
import styled from "styled-components";
import anchorme from "anchorme";
import { stringify } from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RequestService from "domain/consultation-request/requestService";
import { usePracticeArea } from "domain/consultation-request/usePracticeArea";
import languageCodes from "lib/select-options/languages";
import useIsResMobile from "lib/hooks/useIsResMobile";
import Rating from "lib/components/Rating";
import PageLayout from "lib/components/PageLayout";
import useClientDimensions from "lib/hooks/useClientDimensions";
import PromotionIcon from "lib/assets/promotion.svg";
import { useEvent } from "lib/contexts/eventContext";
import { CustomEventLabel } from "lib/enums";
import useSWR from "swr";
import NotFound from "pages/404";
import Head from "lib/components/Head";
import Pagination, {
  PageLink,
  SiblingPageLink,
} from "lib/components/Pagination";
import IconArrow from "lib/assets/arrow-down.svg";
import { purify } from "lib/format";
import { absoluteDate } from "lib/date";
import { useLanguageName, useLocale, useTranslations } from "lib/i18n";

const Lawyer = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
) => {
  const { lawyer } = props;
  const { tag } = useEvent();
  const router = useRouter();
  const isResMobile = useIsResMobile();
  const t = useTranslations("lawyers");
  const practiceArea = usePracticeArea();
  const languageName = useLanguageName();

  /**
   * The API sends ISO 639-1 codes; `languageCodes` supplies the fallback name
   * for anything CLDR does not recognise. The version of this that ran in
   * getServerSideProps indexed the list without a null check, so an unknown code
   * threw rather than degrading.
   */
  const languageLabel = useCallback(
    (code: string) =>
      languageName(
        code,
        languageCodes.find((known) => known.value === code)?.label ?? code,
      ),
    [languageName],
  );
  const { data: lawyerRuntime } = useSWR<ResLawyerProfile>(
    `/lawyers/profiles/${lawyer?.urlSlug}`,
    () => service.getLawyerInfo(lawyer?.lawyerIdString),
    { fallbackData: lawyer },
  );

  const { pageNumber = 1, pageSize = 5 } = router.query;
  const [dataReviews, setDataReviews] = useState<ResLawyerReview>();
  const service = useMemo(() => new RequestService(), []);

  useEffect(() => {
    (async function () {
      const data = await service.getListLawyerReview(
        lawyerRuntime?.lawyerIdString,
        Number(pageSize),
        Number(pageNumber),
      );
      setDataReviews(data);
    })();
  }, [
    lawyerRuntime?.lawyerId,
    lawyerRuntime?.lawyerIdString,
    pageNumber,
    pageSize,
    service,
  ]);

  const {
    profileImageUrl,
    lawyerName,
    aboutLawyer,
    specialityTypes,
    languages,
    website,
    careers,
    schools,
    licences,
    hourlyRate,
    firmName,
    address,
    averageStarRate,
    numOfReviews,
    urlSlug,
    isOnline,
  } = lawyerRuntime || {};

  const websiteLink = anchorme({
    input: website,
    options: {
      attributes: {
        target: "_blank",
      },
    },
  });

  const formattedDescription = aboutLawyer
    ?.replace(/(\r\n|\n|\r)/gm, " ")
    .substring(0, 200)
    .concat("...");
  const consultationRate = Math.round((hourlyRate / 4) * 100) / 100; // 25.9999 -> 25.99
  const reviewRating = Math.round(averageStarRate * 100) / 100; // 3.33333 -> 3.33

  const metaDescription = t("profile.meta.desc");

  // Google Map related vals
  const apiKey = "AIzaSyANMO_aA2EYHfEi0-YMbCpXcp_V29TElWM";
  const addressString = [
    address?.address1,
    address?.address2,
    address?.city,
    address?.state,
  ]
    .filter((address) => address)
    .join(`, `);

  // Review pagination handler
  const handleOnPageChange = useCallback(
    (page: number) => {
      router.push(
        `/lawyers/${urlSlug}?pageSize=${pageSize}&pageNumber=${page}`,
        `/lawyers/${urlSlug}?pageSize=${pageSize}&pageNumber=${page}`,
        { shallow: true },
      );
    },
    [pageSize, router, urlSlug],
  );

  if (props.error === 404) return <NotFound />;
  if (!lawyerRuntime) return null;

  return (
    <PageLayout>
      <Head
        title={`${lawyerName} | Lawfully`}
        desc={metaDescription}
        url={`/lawyers/${urlSlug}`}
        imageUrl={`/share-image-general.jpg`}
      >
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "${lawyerName}",
            "description": "${formattedDescription}",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "${reviewRating}",
              "reviewCount": "${numOfReviews}"
            },
            "offers": {
              "@type": "Offer",
              "url": "${process.env.NEXT_PUBLIC_HOMEPAGE_URL}/lawyers/${urlSlug}/",
              "priceCurrency": "USD",
              "price": "${consultationRate}"
            }
          }
      `}</script>
      </Head>

      <div>
        <div
          css={`
            max-width: 960px;
            margin: ${isResMobile ? 20 : 40}px auto;
            padding: 0 ${isResMobile ? 20 : 50}px;
          `}
        >
          <section>
            <Header>
              {/* use low-res on purpose */}
              <ProfilePicture
                src={profileImageUrl}
                alt={t("profile.profilePictureAlt")}
              />
              <h1
                css={`
                  margin: 10px;
                  font-size: 27px;
                  font-weight: 700;
                  line-height: 1;
                `}
              >
                {lawyerName}
              </h1>
              <div
                css={`
                  display: flex;
                  align-items: center;
                `}
              >
                <Rating rating={reviewRating} hideText gutter={3} size={13} />
                <p
                  css={`
                    margin: 0 0 0 5px;
                    font-size: 12px;
                    color: #777777;
                  `}
                >
                  {t("profile.reviewCount", { count: numOfReviews })}
                </p>
              </div>
              {aboutLawyer && <AboutLawyerContent content={aboutLawyer} />}
            </Header>
          </section>

          <section
            css={`
              margin-top: ${isResMobile ? 20 : 40}px;
              border-radius: 10px;
              padding: ${isResMobile ? 20 : 30}px;
              background-image: ${isOnline
                ? `linear-gradient(283.78deg, #D9E1F2 5.89%, #F1F5FD 95.48%)`
                : `none`};
              display: flex;
              flex-direction: ${isResMobile ? `column` : `row`};
              justify-content: space-between;
              border: ${isOnline ? `none` : `1px solid #C5D7FF`};
            `}
          >
            <DetailContent
              css={`
                margin-top: 0;
                color: #00194d;
                margin-bottom: ${isResMobile ? 0 : 0};
                justify-content: ${isResMobile
                  ? `space-between`
                  : `flex-start`};
                h2 {
                  margin-bottom: ${isResMobile ? 20 : 10}px;
                  line-height: 24px;
                }
              `}
              horizontal={isResMobile ? true : false}
              title={t("profile.videoConsultation")}
              content={
                <div
                  css={`
                    display: flex;
                    flex-direction: column;
                    align-items: ${isResMobile ? `flex-end` : `flex-start`};
                  `}
                >
                  <div
                    css={`
                      display: flex;
                      color: ${lawyer.defaultPaymentFee
                        .previousPromotionAttorneyFee
                        ? `#ff6666`
                        : `inherit`};
                      alignitems: center;
                    `}
                  >
                    {/* promotion icon */}
                    {lawyer.defaultPaymentFee.previousPromotionAttorneyFee && (
                      <PromotionIcon
                        css={`
                          margin-right: 4px;
                        `}
                      />
                    )}
                    {/* final rate */}
                    <div
                      css={`
                        line-height: 24px;
                        font-size: 20px;
                        font-weight: 500;
                      `}
                    >{`$${lawyer.defaultPaymentFee.attorneyFee}`}</div>
                    {/* duration desktop */}
                    <div
                      css={`
                        font-size: 20px;
                        font-weight: 500;
                        display: ${isResMobile ? "none" : "block"};
                        white-space: pre;
                      `}
                    >
                      {` / ${t("profile.duration", { minutes: 15 })}`}
                    </div>
                    {/* original rate */}
                    {lawyer.defaultPaymentFee.previousPromotionAttorneyFee && (
                      <div
                        css={`
                          color: #999999;
                          font-size: 20px;
                          line-height: 28px;
                          font-style: normal;
                          text-decoration: line-through;
                          margin: ${isResMobile ? `0 10px 0 0` : `0 0 0 10px`};
                          order: ${isResMobile ? -1 : 2};
                        `}
                      >
                        {`$${lawyer.defaultPaymentFee.previousPromotionAttorneyFee}`}
                      </div>
                    )}
                  </div>
                  {/* duration mobile */}
                  <div
                    css={`
                      display: ${isResMobile ? "block" : "none"};
                      line-height: 24px;
                      color: #00194d;
                    `}
                  >
                    {t("profile.duration", { minutes: 15 })}
                  </div>
                </div>
              }
            />
            <div
              css={`
                display: flex;
                flex-direction: column;
                justify-content: center;
                min-width: ${isResMobile ? 0 : 280}px;
              `}
            >
              <Link
                href={
                  isOnline && lawyer.lawyerIdString
                    ? `/lawyers/${lawyer.lawyerIdString}/live` // lawyerIdString is unavailable on runtime data
                    : `/lawyers/${lawyer.lawyerIdString}/booking`
                }
                passHref
              >
                <a
                  css={`
                    display: block;
                    border: none;
                    border-radius: 111px;
                    outline: none;
                    background-color: ${isOnline ? `#00194D` : `#195DEE`};
                    font-size: ${isResMobile ? 16 : 18}px;
                    font-weight: 500;
                    cursor: pointer;
                    padding: 21px;
                    text-align: center;
                    color: white;
                    :hover {
                      color: white;
                    }
                  `}
                  className={
                    isOnline
                      ? "ProfileLawyer_Available_Now"
                      : "ProfileLawyer_Book_An_Appointment"
                  }
                  onClick={() =>
                    tag?.(
                      isOnline
                        ? CustomEventLabel.FindALawyerProfileLiveCTA
                        : CustomEventLabel.FindALawyerProfileBookingCTA,
                      {
                        name: lawyer.lawyerName,
                        price: lawyer.defaultPaymentFee.attorneyFee,
                      },
                    )
                  }
                >
                  {isOnline
                    ? t("profile.availableNow")
                    : t("profile.bookAppointment")}
                </a>
              </Link>
              {isOnline && (
                <Link
                  href={`/lawyers/${lawyer.lawyerIdString}/booking`}
                  passHref
                >
                  <a
                    css={`
                      margin-top: 14px;
                      line-height: 24px;
                      text-align: center;
                      font-weight: 500;
                      font-size: 16px;
                      color: inherit;
                      :hover {
                        color: inherit;
                      }
                    `}
                    style={{ textDecoration: "underline" }}
                    onClick={() => {
                      tag?.(CustomEventLabel.FindALawyerProfileBookingCTA, {
                        name: lawyer.lawyerName,
                        price: lawyer.defaultPaymentFee.attorneyFee,
                      });
                    }}
                  >
                    {t("profile.bookAppointment")}
                  </a>
                </Link>
              )}
            </div>
          </section>

          <hr
            css={`
              outline: none;
              border: none;
              border-top: 1px solid #eeeeee;
              margin: ${isResMobile ? 20 : 40}px 0;
            `}
          />

          <section
            css={`
              margin-top: 20px;
            `}
          >
            <div
              css={`
                display: flex;
                flex-wrap: wrap;
              `}
            >
              <DetailContent
                title={t("profile.office")}
                css={`
                  flex-basis: ${isResMobile ? `100%` : `50%`};
                  justify-content: flex-start;
                `}
                content={
                  <InfoRow
                    content={firmName}
                    detail={`${address?.address1}${
                      address?.address2 ? `, ${address?.address2}` : ``
                    }
                ${address?.city}, ${address?.state}
                ${address?.zipcode}
                `}
                  />
                }
              />
              <div
                css={`
                  flex-basis: ${isResMobile ? `100%` : `50%`};
                `}
              >
                <img
                  css={`
                    width: 100%;
                    border-radius: 10px;
                  `}
                  src={`https://maps.googleapis.com/maps/api/staticmap?${stringify(
                    {
                      center: addressString,
                      zoom: 12,
                      size: "400x250",
                      markers: addressString,
                      language: "en",
                      region: "US",
                      scale: 2,
                      key: apiKey,
                    },
                  )}`}
                  alt={t("profile.mapAlt")}
                />
              </div>
            </div>
            <DetailContent
              title={t("profile.practiceAreas")}
              content={specialityTypes.map((type) => (
                <Label key={type.typeId}>{practiceArea.name(type)}</Label>
              ))}
            />
            <DetailContent
              title={t("profile.license")}
              content={licences.map((license) => (
                <InfoRow
                  key={license.state}
                  content={license.state}
                  detail={`${t("profile.acquiredIn", {
                    status: license.status,
                    year: license.acquiredYear,
                  })}${
                    license.updatedDate
                      ? t("profile.updatedIn", {
                          year: new Date(license.updatedDate).getFullYear(),
                        })
                      : ""
                  }`}
                />
              ))}
            />
            <DetailContent
              title={t("profile.career")}
              content={careers.map((career) => (
                <InfoRow
                  key={career.companyName}
                  content={career.companyName}
                  detail={t("profile.careerRange", {
                    title: career.title,
                    from: new Date(career.startDate).getFullYear(),
                    to: career.isPresent
                      ? t("profile.present")
                      : new Date(career.endDate).getFullYear(),
                  })}
                />
              ))}
            />
            <DetailContent
              title={t("profile.school")}
              content={schools.map((school) => (
                <InfoRow
                  key={school.schoolName}
                  content={school.schoolName}
                  detail={t("profile.graduatedIn", {
                    degree: school.degree,
                    year: school.graduateYear,
                  })}
                />
              ))}
            />
            <DetailContent
              title={t("profile.languages")}
              content={languages ? languages.map(languageLabel).join(`, `) : ""}
            />
            {website && (
              <DetailContent
                title={t("profile.website")}
                content={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: purify(websiteLink),
                    }}
                  />
                }
              />
            )}
          </section>

          {dataReviews && (
            <DetailContent
              title={t("profile.reviews")}
              content={
                <div>
                  <ReviewSummary
                    ratingOverall={lawyer.averageStarRate}
                    ratings={{
                      time: dataReviews.averageTimeStar,
                      kindness: dataReviews.averageKindnessStar,
                      expertise: dataReviews.averageExpertiseStar,
                    }}
                    numConsultations={lawyer.numOfConsultations}
                  />
                  <div>
                    {dataReviews.reviews.items.map((review) => (
                      <ReviewCard
                        small={isResMobile}
                        key={review.reviewId}
                        content={review.contents}
                        name={review.clientName}
                        createdAt={review.createdAt}
                        ratings={{
                          time: review.timeStar,
                          kindness: review.kindnessStar,
                          expertise: review.expertiseStar,
                        }}
                      />
                    ))}
                  </div>
                  <Pagination
                    totalCount={dataReviews.reviews.totalCount ?? 0}
                    currentPage={Number(pageNumber) ?? 0}
                    perPageCount={Number(pageSize) ?? 20}
                    renderPage={(pageNumber, isCurrent) => (
                      <PageLink
                        key={pageNumber}
                        selected={isCurrent}
                        onClick={() => handleOnPageChange(pageNumber)}
                      >
                        {pageNumber}
                      </PageLink>
                    )}
                    renderPrev={(currentPage) => (
                      <SiblingPageLink
                        css={`
                          display: flex;
                          align-items: center;
                        `}
                        disabled={!(currentPage > 1)}
                        onClick={() => handleOnPageChange(currentPage - 1)}
                      >
                        <IconArrow
                          style={{
                            transform: "rotate(90deg)",
                            opacity: 0.8,
                          }}
                        />
                        Prev
                      </SiblingPageLink>
                    )}
                    renderNext={(currentPage, numPages) => (
                      <SiblingPageLink
                        css={`
                          display: flex;
                          align-items: center;
                        `}
                        disabled={!(currentPage < numPages)}
                        onClick={() => handleOnPageChange(currentPage + 1)}
                      >
                        Next
                        <IconArrow
                          style={{
                            transform: "rotate(-90deg)",
                            opacity: 0.8,
                          }}
                        />
                      </SiblingPageLink>
                    )}
                  />
                </div>
              }
            />
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default Lawyer;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
`;

const ProfilePicture = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #f1f3f5;
`;

/**
 * Accepts content as string
 * TODO: React to content being updated during runtime. Currently incompatible.
 */
const AboutLawyerContent = ({ content }: { content: string }) => {
  const t = useTranslations("lawyers");
  const ref = useRef(null);
  const { dimension } = useClientDimensions(ref);
  const [isContentLarge, setIsContentLarge] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const LINE_HEIGHT = 22;
  const MAX_LINE = 4;

  const handleOnExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  useEffect(() => {
    if (dimension) {
      if (dimension.height > LINE_HEIGHT * MAX_LINE) {
        setIsContentLarge(true);
      }
    }
  }, [dimension, dimension?.height]);

  return (
    <div
      css={`
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
      `}
    >
      <div
        css={`
          position: relative;
          font-size: 14px;
          line-height: ${LINE_HEIGHT}px;
          max-height: ${isExpanded ? 9999 : LINE_HEIGHT * MAX_LINE}px;
          overflow: hidden;
          display: -webkit-box;
          --wekkit-box-orient: vertical;
        `}
      >
        <div
          ref={ref}
          css={`
            word-wrap: break-word;
            white-space: pre-line;
            margin: 0;
            height: 100%;
          `}
        >
          {content}
        </div>
      </div>
      {isContentLarge && !isExpanded && (
        <ButtonShowMore
          css={`
            position: relative;
          `}
          onClick={handleOnExpand}
        >
          {t("profile.showMore")}
        </ButtonShowMore>
      )}
    </div>
  );
};

const ButtonShowMore = styled.button`
  border: none;
  font-size: 14px;
  line-height: 22px;
  color: #195dee;
  background-color: transparent;
  cursor: pointer;
`;

interface DetailContent {
  title: string;
  content: React.ReactNode;
  className?: string;
  horizontal?: boolean;
}

const DetailContent = ({
  content,
  title,
  className,
  horizontal,
}: DetailContent) => {
  const isResMobile = useIsResMobile();

  return (
    <div className={className}>
      <div
        css={`
          flex-basis: 100%;
          margin-top: 20px;
          margin-bottom: ${isResMobile ? 40 : 60}px;
          display: flex;
          flex-direction: ${horizontal ? `row` : `column`};
          justify-content: space-between;
        `}
      >
        <header>
          <h2
            css={`
              font-size: ${isResMobile ? `20px` : `24px`};
              margin: 0 10px 20px 0;
              line-height: 1.15;
              max-width: ${isResMobile ? `150px` : `300px`};
            `}
          >
            {title}
          </h2>
        </header>
        <div
          css={`
            font-size: 16px;
            text-align: ${horizontal ? `right` : `left`};
            line-height: 1.15;
          `}
        >
          {content}
        </div>
      </div>
    </div>
  );
};

interface InfoRow {
  content: string;
  detail: string;
}

const InfoRow = ({ content, detail }: InfoRow) => (
  <div>
    <p
      css={`
        font-size: 16px;
        margin-top: 0;
        margin-bottom: 0;
      `}
    >
      {content}
    </p>
    <p
      css={`
        marign-top: 5px;
        color: #999999;
        white-space: pre-line;
      `}
    >
      {detail}
    </p>
  </div>
);

const Label = styled.div`
  display: inline-block;
  padding: 5px 10px;
  background-color: #ecf2ff;
  color: black;
  font-size: 14px;
  border-radius: 10px;
  margin-right: 5px;
  margin-bottom: 5px;
`;

const ReviewSummary = ({
  numConsultations,
  ratings,
  ratingOverall,
}: {
  numConsultations: number;
  ratings: ReviewRating;
  ratingOverall: number;
}) => {
  const t = useTranslations("lawyers");
  const isResMobile = useIsResMobile();
  return (
    <div
      css={`
        border-radius: ${isResMobile ? 0 : 10}px;
        background-color: ${isResMobile ? `transparent` : `#F3F4F8`};
        padding: ${isResMobile ? `20px 0 30px` : `30px 60px`};
        margin-bottom: ${isResMobile ? `0` : `30px`};
        border-bottom: ${isResMobile ? `1px solid #EEEEEE` : `none`};
      `}
    >
      <div
        css={`
          display: flex;
          flex-wrap: wrap;
          align-items: center;
        `}
      >
        <div
          css={`
            display: flex;
            flex-basis: ${isResMobile ? `100%` : `50%`};
            margin-bottom: ${isResMobile ? `30px` : `0`};
          `}
        >
          <div
            css={`
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 50%;
              border-right: 1px solid #eeeeee;
            `}
          >
            <ReviewSummaryLabel>
              {t("profile.consultations")}
            </ReviewSummaryLabel>
            <ReviewSummaryNumber>{numConsultations}</ReviewSummaryNumber>
          </div>
          <div
            css={`
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 50%;
            `}
          >
            <ReviewSummaryLabel>{t("profile.rating")}</ReviewSummaryLabel>
            <ReviewSummaryNumber>
              {ratingOverall.toFixed(1)}
            </ReviewSummaryNumber>
          </div>
        </div>
        <div
          css={`
            display: flex;
            flex-basis: ${isResMobile ? `100%` : `50%`};
            flex-direction: column;
          `}
        >
          <RatingRow label={t("profile.useOfTime")} rating={ratings.time} />
          <RatingRow
            label={t("profile.legalKnowledge")}
            rating={ratings.expertise}
          />
          <RatingRow
            label={t("profile.attentiveness")}
            rating={ratings.kindness}
          />
        </div>
      </div>
    </div>
  );
};

const ReviewSummaryLabel = styled.div`
  font-weight: 500;
  font-size: 13px;
  line-height: 13px;
  color: #999999;
  margin-bottom: 12px;
`;

const ReviewSummaryNumber = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 24px;
`;

interface ReviewRating {
  time: number;
  kindness: number;
  expertise: number;
}

interface ReviewCardProps {
  content: string;
  ratings: ReviewRating;
  createdAt: string; // date
  name: string;
  small?: boolean;
}

const ReviewCard = ({
  content,
  ratings,
  name,
  createdAt,
  small,
}: ReviewCardProps) => {
  const t = useTranslations("lawyers");
  const locale = useLocale();
  const isResMobile = useIsResMobile();
  // `ll` reads its pattern from the dayjs locale rather than one written here.
  // It also drops the leading zero the old "MMM DD, YYYY" padded in, so English
  // now reads "Jul 9, 2026" -- which is what CLDR says English does.
  const formattedDate = absoluteDate(createdAt, locale);

  return (
    <div
      css={`
        padding: 30px 0;
        &:not(:first-child) {
          border-top: 1px solid #eeeeee;
        }
      `}
    >
      <div
        css={`
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          margin-bottom: 20px;
        `}
      >
        <div
          css={`
            display: flex;
            flex-basis: ${isResMobile ? `100%` : `auto`};
            margin-bottom: ${isResMobile ? `15px` : `0`};
          `}
        >
          <ReviewName>{name}</ReviewName>
          <ReviewDate>{formattedDate}</ReviewDate>
        </div>
        <div
          css={`
            display: flex;
            flex-basis: ${isResMobile ? `100%` : `auto`};
            align-items: center;
            justify-content: space-between;
          `}
        >
          <RatingRow
            label={t("profile.useOfTime")}
            rating={ratings.time}
            multiline={small}
          />
          <RatingRow
            label={t("profile.legalKnowledge")}
            rating={ratings.expertise}
            multiline={small}
          />
          <RatingRow
            label={t("profile.attentiveness")}
            rating={ratings.kindness}
            multiline={small}
          />
        </div>
      </div>
      <ReviewContent content={content}></ReviewContent>
    </div>
  );
};

const ReviewContent = ({ content }: { content: string }) => {
  const t = useTranslations("lawyers");
  const ref = useRef(null);
  const { dimension } = useClientDimensions(ref);
  const [isContentLarge, setIsContentLarge] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const LINE_HEIGHT = 22;
  const MAX_LINE = 5;

  const handleOnExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  useEffect(() => {
    if (dimension) {
      if (dimension.height > LINE_HEIGHT * MAX_LINE) {
        setIsContentLarge(true);
      }
    }
  }, [dimension, dimension?.height]);

  return (
    <div>
      <div
        css={`
          position: relative;
          font-size: 14px;
          line-height: ${LINE_HEIGHT}px;
          max-height: ${isExpanded ? 9999 : LINE_HEIGHT * MAX_LINE}px;
          overflow: hidden;
          display: -webkit-box;
          --webkit-box-orient: vertical;
        `}
      >
        <p
          ref={ref}
          css={`
            width: 100%;
            height: 100%;
            word-wrap: break-word;
            white-space: pre-line;
            margin: 0;
          `}
        >
          {content}
        </p>
      </div>
      {isContentLarge && !isExpanded && (
        <ButtonShowMore
          css={`
            position: relative;
          `}
          onClick={handleOnExpand}
        >
          {t("profile.showMore")}
        </ButtonShowMore>
      )}
    </div>
  );
};

const RatingRow = ({
  label,
  rating,
  multiline,
}: {
  label: string;
  rating: number;
  multiline?: boolean;
}) => {
  const isResMobile = useIsResMobile();
  return (
    <div
      css={`
        display: flex;
        margin-left: ${isResMobile ? `0` : `13px`};
        justify-content: space-between;
        flex-wrap: wrap;
      `}
    >
      <RatingLabel>{label}</RatingLabel>
      <div
        css={`
          display: flex;
          align-items: center;
          flex-basis: ${multiline ? `100%` : `auto`};
        `}
      >
        <Rating rating={rating} hideText size={11.5} gutter={5} />
      </div>
    </div>
  );
};

const RatingLabel = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  color: #999999;
  margin-right: 10px;
`;

const ReviewName = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 22px;
  margin-right: 12px;
`;

const ReviewDate = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #999999;
`;

export const getServerSideProps = async (context) => {
  try {
    const lawyer = await new RequestService().getLawyerInfo(
      context.params.lawyerId,
    );

    // The codes are passed through untouched and named on the client, where the
    // active locale is known. Resolving them to English labels here is what used
    // to happen, and it left this page as the one place showing a different name
    // for the same language than the directory and the lawyer cards do.
    return { props: { lawyer } };
  } catch (e) {
    if (e.name === "EntityWithStrIdNotFound") {
      return {
        props: { error: 404 },
      };
    }
    return {
      props: { error: 500 },
    };
  }
};
